import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { DropdownActions, DropdownItemBody, DropdownItemCaption, DropdownItemText, DropdownListItem } from '../../../../../widgets/dropdown';
import { DotsIcon } from '../../../../../widgets/icons/scene';
import type { SceneEditorAction } from './sceneEditorActionBar';

// UN ELENCO DI COMANDI CHE SI APRE. Serve in due posti, e per due ragioni opposte:
//
// SULLA RIGA, il «…». I comandi che hanno bisogno di un soggetto — raggruppa, duplica, rinomina,
// rimuovi — stavano nella barra sopra l'elenco, dove agivano su ciò che era selezionato: per
// toccare una riga sola bisognava prima sceglierla, e il comando restava lontano dalla cosa su cui
// cadeva. Ora sta sulla riga, e il soggetto è la riga da cui si apre. Aprire un menu non sceglie
// niente: chi lo costruisce passa quindi i comandi GIÀ legati a quella riga, perché un motore che
// guardasse la selezione lavorerebbe altrove.
//
// IN BARRA, sotto «Add». Aggiungere può avere più strade — un modello arriva da un caricamento o
// dalla libreria, una luce nasce di quattro tipi diversi — e quale lo dice chi clicca. Prima lo
// chiedeva una finestra di dialogo, che è un modulo da compilare prima che la cosa esista.
//
// SI RIUSA IL MENU DELLA PIATTAFORMA (`DropdownActions`, che nasce anche per i tre puntini di una
// riga): chiude con Escape e con un clic fuori, e si posiziona da sé quando sta in fondo
// all'elenco. Riscriverne uno qui sarebbe la stessa cosa scritta due volte.

// I GLIFI DELLA SCENA NON SI DIPINGONO. `DropdownItemIcon` scrive `fill: currentColor` sull'SVG —
// giusto per le icone piene usate altrove nel backoffice, sbagliato qui: i nostri glifi sono
// tracciati con `fill="none"` sulla radice, e riempirli li trasforma in macchie nere.
const CommandGlyph = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    color: currentColor;

    svg {
        width: 18px;
        height: 18px;
    }
`;

// Il pannello di sinistra è largo poco più di duecento pixel: il menu della piattaforma nasce a
// 300 e sborderebbe sempre. Qui basta la larghezza di una voce con la sua riga di spiegazione.
const MENU_MIN_WIDTH = '236px';

const DotsButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: ${(props) => props.theme.app.textColor};
    opacity: 0.72;
    cursor: pointer;

    svg {
        width: 16px;
        height: 16px;
    }

    &:hover,
    &:focus-visible {
        opacity: 1;
        background: ${(props) => props.theme.app.borderColor};
        outline: none;
    }
`;

export interface CommandMenuProps {
    /** Già legati al soggetto: chi li costruisce sa su cosa devono cadere. */
    actions: SceneEditorAction[];
    /** Chi apre il menu. Senza, è il «…» di una riga. */
    trigger?: ReactNode;
    /**
     * DA CHE PARTE SI APRE. Il «…» sta al bordo destro della riga e si apre verso sinistra; «Add» sta
     * al bordo sinistro della barra e si apre verso destra — aperto verso sinistra copriva il rail.
     */
    orientation?: 'LEFT' | 'RIGHT';
}

const CommandMenu: FC<CommandMenuProps> = ({ actions, trigger, orientation = 'RIGHT' }) => {
    const usable = actions.filter((action) => !action.disabled);

    // Un menu vuoto è un bottone che non fa niente: meglio non offrirlo.
    if (!usable.length) return null;

    return (
        <DropdownActions
            orientation={orientation}
            minWidth={MENU_MIN_WIDTH}
            header={
                trigger ?? (
                    <DotsButton type='button' aria-label={T._('More commands', 'Admin')} title={T._('More commands', 'Admin')}>
                        <DotsIcon />
                    </DotsButton>
                )
            }
            items={usable.map((action) => (
                <DropdownListItem
                    key={action.name}
                    type='button'
                    $danger={action.kind === 'danger'}
                    // IL CLIC DEVE SALIRE. È il pannello del menu, un gradino sopra, a chiudere
                    // l'elenco quando si sceglie una voce — e a fermare lì il clic, perché la riga
                    // sotto non creda di essere stata premuta. Fermandolo QUI si otteneva un menu
                    // che restava aperto: il comando partiva e l'elenco non se ne andava più, così
                    // chi non vedeva succedere niente cliccava di nuovo.
                    onClick={() => action.onClick()}
                >
                    <CommandGlyph aria-hidden='true'>{action.icon}</CommandGlyph>
                    <DropdownItemBody>
                        <DropdownItemText>{action.name}</DropdownItemText>
                        {/* IL «…» DI UNA RIGA DICE SOLO IL NOME (utente, 2026-10-15): sono comandi che si capiscono da
                            soli. Il menu di «Add» tiene la riga sotto, perché lì si sceglie fra cose diverse. */}
                        {trigger && action.tooltip && action.tooltip !== action.name && <DropdownItemCaption>{action.tooltip}</DropdownItemCaption>}
                    </DropdownItemBody>
                </DropdownListItem>
            ))}
        />
    );
};

export default CommandMenu;
